import type {
  FunnelDefinitionNode,
  FunnelDefinitionRootNode,
  Flow,
  Question
} from '@/types/config'
import { doesCodeReturnValue } from '@rightmessage/flow-builder'

export const isEmptyFunnel = (
  funnelDefinition: FunnelDefinitionRootNode
): boolean => {
  return funnelDefinition.child.length === 0
}

export const flowHasQuestions = (
  funnelDefinition: FunnelDefinitionRootNode
) => {
  let hasQuestions = false
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask) {
      hasQuestions = true
    }
  })
  return hasQuestions
}

export const isNodeSetUp = (node: FunnelDefinitionNode): boolean => {
  if (node.type === 'sg') {
    return node.segmentGroupId !== null
  }
  if (node.type === 'yesno') {
    return JSON.stringify(node.condition) !== JSON.stringify({ or: [] })
  }
  if (node.type === 'offer') {
    const offer = useOfferStore().getById(node.offerId)
    if (offer && isForm(offer) && !offerCanBeSubmitted(offer)) {
      return false
    }
    return node.offerId !== null
  }
  if (node.type === 'redirect') {
    return node.url !== ''
  }
  if (node.type === 'code') {
    return node.code !== ''
  }
  if (node.type === 'ai') {
    return false
  }
  return true
}

// Re-export doesCodeReturnValue from the package for convenience
export { doesCodeReturnValue }

export const doesNodeTerminate = (node: FunnelDefinitionNode): boolean => {
  if (node.type === 'sg' && node.split && Array.isArray(node.split)) {
    if (node.segmentGroupId && node.split.length) {
      return node.split.every((split: any) =>
        split.child.some((child: any) => doesNodeTerminate(child))
      )
    } else {
      return false
    }
  }
  if (node.type === 'yesno') {
    return (
      node.yes.some((child: any) => doesNodeTerminate(child)) &&
      node.no.some((child: any) => doesNodeTerminate(child))
    )
  }
  if (node.type === 'random') {
    return node.split.every((split: any) =>
      split.child.some((child: any) => doesNodeTerminate(child))
    )
  }

  // Code nodes terminate the flow if they return a value
  if (node.type === 'code') {
    return doesCodeReturnValue(node.code)
  }

  return (
    node.type === 'exit' ||
    node.type === 'offer' ||
    node.type === 'redirect' ||
    node.type === 'message'
  )
}

export const questionsInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  const questionStore = useQuestionStore()
  let questions: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask && node.segmentGroupId) {
      questions.push(node.segmentGroupId)
    }
  })

  return questions
    .map((qId) => {
      // For input questions (raq_*), get by ID directly
      if (qId.startsWith('raq_')) {
        return questionStore.getById(qId)
      }
      // For regular questions linked to dimensions
      return questionStore.getBySegmentGroupId(qId)
    })
    .filter((q) => q !== undefined) // Filter out any undefined results
    .map((q) => ({
      id: q.id,
      label: stripHtml(renderText(q.text)),
      ...q
    }))
}

export const offersInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  const offerStore = useOfferStore()
  let offers: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'offer' && node.offerId) {
      offers.push(node.offerId)
    }
  })

  const OffersGottenById = offers.map((o) => offerStore.getById(o))

  return OffersGottenById.length
    ? OffersGottenById.filter(Boolean).map((o) => ({
        id: o.id,
        label: o.name,
        ...o
      }))
    : []
}

export const redirectsInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  let redirects: any[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'redirect' && node.url !== '') {
      redirects.push(node)
    }
  })
  return redirects
}

export const visibleThingsInFlow = (
  funnelDefinition: FunnelDefinitionRootNode
) => {
  return [
    ...questionsInFlow(funnelDefinition),
    ...offersInFlow(funnelDefinition)
  ]
}

export const dropdownFlows = computed(() => {
  const flowStore = useFlowStore()
  const flows = flowStore.flows
  return flows
    .map((flow) => ({
      label: flow.name,
      value: flow.id
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

export const flowContainsQuestion = (flow: Flow, question: Question) => {
  const questions = questionsInFlow(flow.funnelDefinition)
  return questions.some((q) => q.id === question.id)
}

export const flowErrors = (flow: Flow) => {
  const errors: string[] = []

  // System flows don't need widgets
  if (!flow.isSystemFlow) {
    const widgets = useWidgetStore().getWidgetsForOfferFunnel(flow.id)
    if (widgets.length === 0) {
      errors.push("This flow isn't attached to a widget yet")
    }
  }

  // System flows don't need questions or offers (they can only have Action nodes)
  if (!flow.isSystemFlow) {
    if (
      offersInFlow(flow.funnelDefinition).length === 0 &&
      questionsInFlow(flow.funnelDefinition).length === 0
    ) {
      if (redirectsInFlow(flow.funnelDefinition).length > 0) {
        errors.push(
          'Heads up: Visitors will be redirected without seeing any content'
        )
      } else {
        errors.push('Add at least one question or offer to get started')
      }
    }
  }

  funnelDefinitionEach(flow.funnelDefinition, (node) => {
    if (!isNodeSetUp(node)) {
      if (node.type === 'sg' && node.ask && node.segmentGroupId === null) {
        errors.push('Choose which question to ask visitors')
      }
      if (
        node.type === 'yesno' &&
        JSON.stringify(node.condition) === JSON.stringify({ or: [] })
      ) {
        errors.push('Set up your branching rules')
      }
      if (node.type === 'offer' && node.offerId === null) {
        if (node.desiredType === 'form') {
          errors.push('Select which form to show')
        } else {
          errors.push('Choose what content to display')
        }
      }
      if (node.type === 'redirect' && node.url === '') {
        errors.push('Add the webpage where visitors should go')
      }
      if (node.type === 'code' && node.code === '') {
        errors.push('Add your custom code')
      }
    }
  })
  return errors
}

// Enhanced version that returns error objects with node references
export const flowErrorsWithNodes = (flow: Flow) => {
  const errors: Array<{ message: string; node?: FunnelDefinitionNode }> = []

  // System flows don't need widgets
  if (!flow.isSystemFlow) {
    const widgets = useWidgetStore().getWidgetsForOfferFunnel(flow.id)
    if (widgets.length === 0) {
      errors.push({ message: "This flow isn't attached to a widget yet" })
    }
  }

  // System flows don't need questions or offers (they can only have Action nodes)
  if (!flow.isSystemFlow) {
    if (
      offersInFlow(flow.funnelDefinition).length === 0 &&
      questionsInFlow(flow.funnelDefinition).length === 0
    ) {
      if (redirectsInFlow(flow.funnelDefinition).length > 0) {
        errors.push({
          message:
            'Heads up: Visitors will be redirected without seeing any content'
        })
      } else {
        errors.push({
          message: 'Add at least one question or offer to get started'
        })
      }
    }
  }

  funnelDefinitionEach(flow.funnelDefinition, (node) => {
    if (!isNodeSetUp(node)) {
      if (node.type === 'sg' && node.ask && node.segmentGroupId === null) {
        errors.push({ message: 'Choose which question to ask visitors', node })
      }
      if (
        node.type === 'yesno' &&
        JSON.stringify(node.condition) === JSON.stringify({ or: [] })
      ) {
        errors.push({ message: 'Set up your branching rules', node })
      }
      if (node.type === 'offer' && node.offerId === null) {
        if (node.desiredType === 'form') {
          errors.push({ message: 'Select which form to show', node })
        } else {
          errors.push({ message: 'Choose what content to display', node })
        }
      }
      if (node.type === 'redirect' && node.url === '') {
        errors.push({
          message: 'Add the webpage where visitors should go',
          node
        })
      }
      if (node.type === 'code' && node.code === '') {
        errors.push({ message: 'Add your custom code', node })
      }
    }
  })
  return errors
}
